import { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { sectionPath } from "../utils/routePaths";
import { resolveSlug } from "../utils/slugify";
import { optimizedImageUrl } from "../utils/cloudinaryUrl";

const ACCENT = "#C47C2B";
const DARK = "#1A1A18";

function AdvCard({ adv, language, onDragStart }) {
  const [hovered, setHovered] = useState(false);
  const mouseDownX = useRef(0);
  const name = language === "ka" && adv.nameKa ? adv.nameKa : adv.name;
  const desc = language === "ka" && adv.descriptionKa ? adv.descriptionKa : adv.description;

  return (
    <Link
      to={sectionPath(language, "adventures", resolveSlug(adv, language))}
      draggable={false}
      style={{
        flexShrink: 0, width: 340, height: 300, borderRadius: 20,
        overflow: "hidden", display: "block", position: "relative",
        background: "#1A1A18", textDecoration: "none",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "transform 0.3s, box-shadow 0.3s",
        boxShadow: hovered ? "0 8px 24px rgba(0,0,0,0.14)" : "0 2px 8px rgba(0,0,0,0.08)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(e) => {
        mouseDownX.current = e.clientX;
        onDragStart(e);
      }}
      onClick={(e) => {
        if (Math.abs(e.clientX - mouseDownX.current) > 5) {
          e.preventDefault();
        }
      }}
    >
      {adv.image && (
        <img src={optimizedImageUrl(adv.image, 600)} alt={name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.5s ease" }} />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(5,8,15,0.92) 0%, rgba(5,8,15,0.3) 55%, transparent 100%)" }} />

      {/* Top badge */}
      <div style={{ position: "absolute", top: 16, left: 16, display: "flex", alignItems: "center", gap: 6, background: "rgba(10,12,18,0.6)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, padding: "6px 14px" }}>
        {adv.emoji && <span style={{ fontSize: 13 }}>{adv.emoji}</span>}
        <span style={{ fontFamily: "Geist, sans-serif", fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.04em" }}>{name}</span>
      </div>

      {/* Bottom */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "18px 20px", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <div style={{ flex: 1, marginRight: 12 }}>
          <h3 style={{ fontFamily: "Geist, sans-serif", fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 6, lineHeight: 1.2, letterSpacing: "-0.01em" }}>{name}</h3>
          {desc && (
            <p style={{ fontFamily: "Geist, sans-serif", fontSize: 13, color: "rgba(255,255,255,0.65)", margin: 0, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {desc}
            </p>
          )}
        </div>
        <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transform: hovered ? "scale(1.1)" : "scale(1)", transition: "transform 0.2s" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 17, color: "#1A1A18" }}>arrow_forward</span>
        </div>
      </div>
    </Link>
  );
}

export default function AdventuresSection() {
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { language, t } = useLanguage();

  const scrollRef  = useRef(null);
  const isDragging = useRef(false);
  const startX     = useRef(0);
  const scrollStart = useRef(0);
  const hasDragged = useRef(false);

  useEffect(() => {
    getDocs(collection(db, "adventureCategories"))
      .then((snap) => setAdventures(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* ── arrow visibility ── */
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateArrows(); ticking = false; });
    };
    requestAnimationFrame(updateArrows);
    el.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", onScroll); ro.disconnect(); };
  }, [adventures, loading, updateArrows]);

  const doScrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  /* ── drag to scroll ── */
  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    isDragging.current  = true;
    hasDragged.current  = false;
    startX.current      = e.clientX;
    scrollStart.current = scrollRef.current?.scrollLeft ?? 0;
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - startX.current;
      if (Math.abs(dx) > 3) hasDragged.current = true;
      if (scrollRef.current) scrollRef.current.scrollLeft = scrollStart.current - dx;
      document.body.style.cursor = "grabbing";
    };
    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
  }, []);

  if (!loading && adventures.length === 0) return null;

  const SIDE_PAD = "6vw";

  return (
    <section style={{ background: "transparent", paddingTop: "clamp(40px, 5vw, 72px)", paddingBottom: "clamp(40px, 5vw, 72px)" }}>

      {/* Header */}
      <div style={{ padding: `0 ${SIDE_PAD}`, marginBottom: 24, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={ACCENT} strokeWidth="2" strokeLinejoin="round" fill="none"/>
            </svg>
            <span style={{ fontFamily: "Geist, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-text)" }}>
              {t("adventures_eyebrow") || (language === "ka" ? "აქტიური დასვენება" : "Active Adventures")}
            </span>
          </div>
          <h2 style={{ fontFamily: "Geist, sans-serif", fontSize: "clamp(22px, 2.8vw, 34px)", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--text-primary)", margin: "0 0 6px" }}>
            {t("adventures_title") || (language === "ka" ? "გამოიკვლიე თავგადასავლები" : "Explore Adventures")}
          </h2>
          <p style={{ fontFamily: "Geist, sans-serif", fontSize: 14, color: "var(--text-secondary)", margin: 0, maxWidth: 440 }}>
            {t("adventures_subtitle") || (language === "ka" ? "აირჩიე აქტივობა და აღმოაჩინე დაუვიწყარი გამოცდილება" : "Choose an activity and discover unforgettable experiences across Svaneti.")}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="adv-arrows" style={{ display: "flex", gap: 8 }}>
            <button onClick={() => doScrollBy(-1)} disabled={!canScrollLeft}
              style={{ width: 38, height: 38, borderRadius: "50%", border: "1.5px solid var(--border)", background: "var(--bg-surface)", cursor: canScrollLeft ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", opacity: canScrollLeft ? 1 : 0.35, transition: "opacity 0.2s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 17, color: "var(--text-primary)" }}>arrow_back</span>
            </button>
            <button onClick={() => doScrollBy(1)} disabled={!canScrollRight}
              style={{ width: 38, height: 38, borderRadius: "50%", border: "1.5px solid var(--border)", background: "var(--bg-surface)", cursor: canScrollRight ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", opacity: canScrollRight ? 1 : 0.35, transition: "opacity 0.2s" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 17, color: "var(--text-primary)" }}>arrow_forward</span>
            </button>
          </div>
          <Link to={sectionPath(language, "adventures")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1.5px solid var(--border)", color: "var(--text-primary)", padding: "9px 18px", borderRadius: 8, fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap", transition: "all 0.2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-inverse)"; e.currentTarget.style.color = "var(--fg-inverse)"; e.currentTarget.style.borderColor = "var(--bg-inverse)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--border)"; }}>
            {t("adventures_view_all") || (language === "ka" ? "ყველა" : "View All")}
            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>arrow_forward</span>
          </Link>
        </div>
      </div>

      {/* Scroll row */}
      <div style={{ paddingLeft: SIDE_PAD, paddingRight: SIDE_PAD }}>
        <div
          ref={scrollRef}
          className="adv-scroll"
          style={{
            display: "flex", gap: 16,
            overflowX: "auto", overflowY: "visible",
            scrollbarWidth: "none", WebkitOverflowScrolling: "touch",
            cursor: "grab", userSelect: "none",
            paddingTop: 10, paddingBottom: 28,
          }}
        >
          {loading
            ? Array(3).fill(0).map((_, i) => (
                <div key={i} style={{ flexShrink: 0, width: 340, height: 300, borderRadius: 20, background: "var(--bg-surface-alt)", animation: "advShimmer 1.5s infinite" }} />
              ))
            : adventures.map((adv) => (
                <AdvCard key={adv.id} adv={adv} language={language} onDragStart={onDragStart} />
              ))
          }
        </div>
      </div>

      <style>{`
        @keyframes advShimmer { 0%,100%{opacity:.6} 50%{opacity:1} }
        .adv-scroll::-webkit-scrollbar { display: none; }
        .adv-scroll > * { scroll-snap-align: start; }
        @media (max-width: 600px) {
          .adv-arrows { display: none !important; }
        }
      `}</style>
    </section>
  );
}
